import { ApolloError } from '@apollo/client';
import { useListStringGroupVersionsLazyQuery } from 'src/generated/graphql';
import { useEffect } from 'react';
import { joinList } from 'src/common/utils/joinList';
import { useTranslation, type TranslateFn } from 'src/i18n/shim/useTranslation';
import { StringGroupVersionSummary } from '../types';

/** Exported because the panel's "Showing last N versions" notice quotes it; the figure was
 * previously a literal 25 in that copy, so the two could drift. */
export const VERSION_HISTORY_WINDOW = 25;

interface UseVersionHistoryResult {
  data: StringGroupVersionSummary[] | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Lazily fetches version history for a string group. Only loads data when
 * `enabled` is true (i.e., the version history panel is open).
 * Uses a lazy query to avoid fetching on mount — only fires when needed.
 */
export function useVersionHistory(
  enabled: boolean,
  namespaceId?: string,
  stringGroupId?: string,
): UseVersionHistoryResult {
  const { t, locale } = useTranslation();
  const [fetchVersions, { data, loading, error }] =
    useListStringGroupVersionsLazyQuery({
      fetchPolicy: 'cache-and-network',
    });

  useEffect(() => {
    if (enabled && namespaceId && stringGroupId) {
      fetchVersions({
        variables: {
          namespaceId,
          stringGroupId,
          sortOrder: 'DESC',
          maxResults: VERSION_HISTORY_WINDOW,
        },
      });
    }
  }, [enabled, namespaceId, stringGroupId, fetchVersions]);

  const mapped: StringGroupVersionSummary[] | null = data
    ? data.listStringGroupVersions.versions.map((v, index, versions) => {
        const cxIds = v.cxIds ?? [];
        // Versions arrive sorted DESC, so the NEXT entry is this version's
        // predecessor. A CX ID change is reported relative to it, mirroring the
        // backend's added/edited/deleted delta. The oldest fetched version has no
        // predecessor in the window, so its CX change cannot be told apart from
        // "unchanged" and is treated as no change (same boundary the delta counts
        // hit at v1).
        const previous = versions[index + 1];
        const cxIdsChanged = cxIdsDiffer(cxIds, previous?.cxIds);
        // Fall back to zero counts when the backend sends no per-version delta
        // (absent for backfilled / non-LangHub versions).
        const counts = {
          addedCount: v.changeSummary?.addedCount ?? 0,
          editedCount: v.changeSummary?.editedCount ?? 0,
          deletedCount: v.changeSummary?.deletedCount ?? 0,
        };
        // Emit a change summary when the backend supplied one OR the CX IDs
        // changed. The latter is what lets a CX-only version whose backend
        // `changeSummary` is null/absent still read "CX ID edited" instead of
        // falling through to the bare "Version N" label. A version with neither
        // keeps no summary, so the panel's "Version N" fallback still applies.
        const changeSummary =
          v.changeSummary || cxIdsChanged
            ? {
                ...counts,
                description: formatChangeSummary(
                  t,
                  locale,
                  v.version,
                  counts,
                  cxIdsChanged,
                ),
              }
            : undefined;
        return {
          // Defensive coercion, so downstream strict-equality version checks
          // hold even if a string reaches us.
          version: Number(v.version),
          translationStatus: v.translationStatus,
          stringCount: v.stringCount,
          cxIds,
          updatedOn: v.updatedOn ?? 0,
          updatedBy: v.updatedBy ?? '',
          cancelRequestedOn: v.cancelRequestedOn ?? undefined,
          cancelRequestedBy: v.cancelRequestedBy ?? undefined,
          changeSummary,
        };
      })
    : null;

  return {
    data: mapped,
    loading,
    error: toError(error),
  };
}

/**
 * Whether this version's CX IDs differ from its predecessor's. Order-insensitive
 * (CX IDs are a set, not a sequence). Returns false when there is no predecessor
 * to compare against — absence of history is not a change.
 */
function cxIdsDiffer(
  current: string[],
  previous: string[] | undefined,
): boolean {
  if (!previous) {
    return false;
  }
  if (current.length !== previous.length) {
    return true;
  }
  const previousSet = new Set(previous);
  return current.some((id) => !previousSet.has(id));
}

/** One declaration for both the clause order and the id set, so the three kinds cannot drift. */
const CHANGE_CLAUSE_IDS: readonly (readonly [
  count: keyof ChangeCounts,
  id: string,
])[] = [
  ['addedCount', 'sgd-change-strings-added'],
  ['editedCount', 'sgd-change-strings-edited'],
  ['deletedCount', 'sgd-change-strings-removed'],
];

interface ChangeCounts {
  addedCount: number;
  editedCount: number;
  deletedCount: number;
}

/** e.g. "2 strings added and 1 string edited", or "CX ID changed" on a CX-only version. */
function formatChangeSummary(
  t: TranslateFn,
  locale: string,
  version: number,
  summary: ChangeCounts,
  cxIdsChanged: boolean,
): string {
  if (
    version === 1 &&
    summary.addedCount > 0 &&
    summary.editedCount === 0 &&
    summary.deletedCount === 0
  ) {
    return t('sgd-change-strings-added-initial', {
      count: summary.addedCount,
    });
  }

  // Standardized change vocabulary shared with the string detail page: added,
  // edited, removed.
  const parts: string[] = [];
  for (const [countKey, id] of CHANGE_CLAUSE_IDS) {
    const count = summary[countKey];
    if (count > 0) {
      parts.push(t(id, { count }));
    }
  }

  // Appended as a further list item rather than through a "{changes}, and …" frame, so the
  // conjunction stays `joinList`'s responsibility. Reuses the string detail page's resource.
  if (cxIdsChanged) {
    parts.push(t('string-change-cxid'));
  }

  return parts.length > 0 ? joinList(locale, parts) : t('sgd-change-none');
}

/**
 * Normalises Apollo's `undefined` to `null`, and otherwise hands the
 * `ApolloError` straight through.
 *
 * It must NOT be rebuilt as a plain `new Error(error.message)`: `ApolloError`
 * already IS an `Error`, and rebuilding it drops `graphQLErrors` — the only place
 * the AppSync `errorType` lives — which defeats `isAccessDenied` at the consumer
 * and makes every refusal render as a fault.
 */
function toError(error: ApolloError | undefined): Error | null {
  return error ?? null;
}
